import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Editorial theme colors
const COLORS = {
  cream: '#FAF8F5',
  charcoal: '#2D2A26',
  terracotta: '#C45D3A',
  border: '#2D2A26'
};

const SectionContainer = styled(Box)({
  backgroundColor: COLORS.cream,
  borderBottom: `1px solid ${COLORS.charcoal}`
});

const SectionHeader = styled(Box)({
  padding: '48px 64px 32px',
  borderBottom: `1px solid ${COLORS.charcoal}`,
  '@media (max-width: 768px)': {
    padding: '32px 20px 24px'
  }
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '42px',
  fontWeight: 700,
  color: COLORS.charcoal,
  letterSpacing: '-1px',
  '@media (max-width: 768px)': {
    fontSize: '32px'
  }
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginTop: '8px'
});

const ServicesGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr'
  }
});

const ServiceCard = styled(Box)({
  padding: '40px 32px',
  borderRight: `1px solid ${COLORS.charcoal}`,
  borderBottom: `1px solid ${COLORS.charcoal}`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: COLORS.charcoal,
    '& .service-icon': {
      backgroundColor: COLORS.terracotta,
      borderColor: COLORS.terracotta,
      color: COLORS.cream
    },
    '& .service-title': {
      color: COLORS.cream
    },
    '& .service-description': {
      color: `${COLORS.cream}99`
    },
    '& .service-price': {
      color: COLORS.terracotta
    }
  },
  '&:nth-of-type(3n)': {
    borderRight: 'none',
    '@media (max-width: 1024px)': {
      borderRight: `1px solid ${COLORS.charcoal}`,
      '&:nth-of-type(2n)': {
        borderRight: 'none'
      }
    },
    '@media (max-width: 600px)': {
      borderRight: 'none'
    }
  },
  '@media (max-width: 1024px)': {
    '&:nth-of-type(2n)': {
      borderRight: 'none'
    }
  },
  '@media (max-width: 600px)': {
    borderRight: 'none'
  }
});

const ServiceIcon = styled(Box)({
  width: '64px',
  height: '64px',
  border: `1px solid ${COLORS.charcoal}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
  transition: 'all 0.3s ease',
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '28px',
  fontWeight: 700,
  color: COLORS.charcoal
});

const ServiceTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '22px',
  fontWeight: 600,
  color: COLORS.charcoal,
  marginBottom: '12px',
  transition: 'color 0.3s ease'
});

const ServiceDescription = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.7,
  lineHeight: 1.7,
  marginBottom: '16px',
  transition: 'color 0.3s ease',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
});

const ServicePrice = styled(Typography)({
  fontFamily: "'Space Mono', monospace",
  fontSize: '18px',
  fontWeight: 700,
  color: COLORS.terracotta,
  transition: 'color 0.3s ease'
});

const EditorialServicesSection = ({ services }) => {
  // Take first 6 services
  const displayServices = services?.slice(0, 6) || [];

  if (displayServices.length === 0) {
    return null;
  }

  return (
    <SectionContainer id="services">
      <SectionHeader>
        <SectionSubtitle>What We Offer</SectionSubtitle>
        <SectionTitle>Services & Products</SectionTitle>
      </SectionHeader>

      <ServicesGrid>
        {displayServices.map((service, index) => (
          <ServiceCard key={service.id || index}>
            <ServiceIcon className="service-icon">
              {(service.name || service.title || 'S').charAt(0).toUpperCase()}
            </ServiceIcon>

            <ServiceTitle className="service-title">
              {service.name || service.title || 'Service'}
            </ServiceTitle>

            {service.description && (
              <ServiceDescription className="service-description">
                {service.description}
              </ServiceDescription>
            )}

            {service.price && (
              <ServicePrice className="service-price">
                {typeof service.price === 'number' ? `₹${service.price}` : service.price}
              </ServicePrice>
            )}
          </ServiceCard>
        ))}
      </ServicesGrid>
    </SectionContainer>
  );
};

export default EditorialServicesSection;
